<template>
  <ion-page :style="backgroundColorStyle">
    <slot name="sidebar" />
    <ion-header
      v-if="$slots.header"
      class="ion-no-border header-transparent"
      style="--background: transparent; background: transparent"
    >
      <slot name="header" />
    </ion-header>
    <ion-header v-else />
    <ion-content
      :id="id"
      ref="ionContentRef"
      :class="['master', { 'hide-native-scrollbar': scrollProgressSupported }]"
      :scroll-events="enableScrollEvent"
      @ionScroll="onScroll"
      :style="backgroundColorStyle"
    >
      <!-- Ionic's `fixed` slot keeps this out of the scrolling area. The slot
           lives on a plain element so the assignment can't be lost to Vue
           attribute fallthrough, and it spans the content box so the bar has
           something full-height to position against. -->
      <div v-if="contentEl" slot="fixed" class="absolute inset-0 pointer-events-none">
        <ScrollProgressBar :content-el="contentEl" :measure-el="measureEl" />
      </div>
      <!-- Pull-to-refresh. Must be a DIRECT child of ion-content, which rules
           out the default slot (that lands inside .page-transition-content), so
           pages that want a refresher get their own slot here. Renders nothing
           when unused. -->
      <slot name="refresher" />
      <Loading v-if="showLoading" />
      <div class="page-transition-content" data-page-transition>
        <!--
          Width is owned by the `.responsive-main` media queries in the scoped
          block below, NOT by Tailwind utilities. This used to also carry
          `md:max-w-4xl lg:max-w-xl` (896 -> 576, a shrink), which contradicted
          those media queries (896 -> 1280) and never applied: the scoped rules
          carry a data-v attribute and win on specificity. Two sources of truth,
          one of them dead — the utilities are removed so the media queries are
          the only place page width is decided.
        -->
        <main
          ref="responsiveMainRef"
          class="responsive-main flex flex-col w-full"
          :class="{ '!max-w-none': fullWidth, 'responsive-main--tab-padding': tabPadding }"
        >
          <slot class="flex-1 min-h-full" />
        </main>
      </div>
    </ion-content>
    <ion-footer
      v-if="showFooter && !$route.meta.hideMasterLayoutFooter"
      class="ion-no-border footer-transparent"
      style="
        --background: transparent;
        background: transparent;
        --ion-background-color: transparent;
        padding: 0;
      "
    >
      <slot name="footer" />
    </ion-footer>
  </ion-page>
</template>

<script>
import { markRaw } from 'vue';
import { IonPage, IonHeader, IonContent, IonFooter } from '@ionic/vue';
import { mapGetters, mapState } from 'vuex';
import Loading from '@/components/common/LoadingState';
import ScrollProgressBar from '@/components/common/ScrollProgressBar.vue';
import { isIosNativeOnly } from '@/utils/platform-check';

function updateBodyOverflow(isHidden) {
  if (isHidden) {
    document.body.classList.add('overflow-hidden');
  } else {
    document.body.classList.remove('overflow-hidden');
  }
}

export default {
  components: {
    IonPage,
    IonHeader,
    IonContent,
    IonFooter,
    Loading,
    ScrollProgressBar,
  },
  props: {
    id: {
      type: String,
      default: 'ion-content',
    },
    enableScrollEvent: {
      type: Boolean,
      default: false,
    },
    noLogin: {
      type: Boolean,
      default: false,
    },
    showFooter: {
      type: Boolean,
      default: false,
    },
    fullWidth: {
      type: Boolean,
      default: false,
    },
    loading: {
      type: Boolean,
      default: null,
    },
    backgroundColor: {
      type: String,
      default: null,
    },
    tabPadding: {
      type: Boolean,
      default: false,
    },
    // Opt-in right-edge scroll indicator (see ScrollProgressBar.vue). Off by
    // default so existing pages are unaffected.
    showScrollProgress: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      scrollPosition: 0,
      // Resolved in mounted() for ScrollProgressBar, which needs the scrolling
      // element and the one wrapper that grows with the content. markRaw keeps
      // them out of the reactivity proxy — ResizeObserver rejects a Proxy.
      contentEl: null,
      measureEl: null,
    };
  },
  computed: {
    ...mapState('lookups', ['pastoralMapping']),
    ...mapGetters('profile', ['pastoralRoleId']),
    ...mapState('profile', ['showingProfileModal']),
    isIos() {
      return isIosNativeOnly();
    },
    showLoading() {
      return this.loading === true;
    },
    // ScrollProgressBar learns about new content from a ResizeObserver, which
    // iOS only gained in 13.4 — below that it would render a bar that never
    // updates. Since we also hide the platform indicator wherever ours is
    // drawn, do neither on those versions and leave iOS's own bar in place.
    scrollProgressSupported() {
      return this.showScrollProgress && typeof ResizeObserver !== 'undefined';
    },
    backgroundColorStyle() {
      if (this.backgroundColor) {
        return {
          '--background': this.backgroundColor,
          '--background-color': this.backgroundColor,
          background: this.backgroundColor,
        };
      }
      return {};
    },
  },
  watch: {
    showingProfileModal(isShowing) {
      updateBodyOverflow(isShowing);
    },
  },
  async mounted() {
    if (this.scrollProgressSupported) {
      this.contentEl = markRaw(this.$refs.ionContentRef.$el);
      this.measureEl = markRaw(this.$refs.responsiveMainRef);
    }
    if (!this.noLogin) {
      await Promise.all([
        this.$store.dispatch('profile/fetchProfile'),
        this.$store.dispatch('lookups/fetchPastoralMapping'),
        this.$store.dispatch('profile/fetchMinistryStatus'),
        this.$store.dispatch('lookups/fetchProfileDropdowns'),
      ]);
    }
  },
  methods: {
    async onScroll(event) {
      this.$emit('on-scroll', event);

      const scrollElement = await event.target.getScrollElement();
      this.scrollPosition = scrollElement.scrollTop;

      // Emit scroll position for sticky headers (library pages use this)
      this.$emit('scroll-position', this.scrollPosition);
    },
    async lockScroll() {
      const content = await this.$refs.ionContentRef.$el.getScrollElement();
      if (content) {
        content.style.overflow = 'hidden';
        content.style.touchAction = 'none';
      }
    },
    async unlockScroll() {
      const content = await this.$refs.ionContentRef.$el.getScrollElement();
      if (content) {
        content.style.overflow = '';
        content.style.touchAction = '';
      }
    },
  },
};
</script>

<style scoped>
ion-footer {
  --border-color: transparent;
  --background: transparent;
  --ion-color-base: transparent !important;
}

ion-toolbar {
  --background: transparent;
  --ion-color-base: transparent !important;
}

.toolbar-background {
  background: transparent;
}

ion-content.master {
  --padding-bottom: 0px;
  --background: #f6f5f1;
  --background-color: #f6f5f1;
  background: #f6f5f1;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.footer-md::before {
  display: none;
}

.box-shadow {
  box-shadow: 0px 1px 25px 0px #0000001a;
}

/* Remove box-shadow from footer toolbar for glassmorphism effect */
ion-footer .box-shadow,
ion-footer.footer-transparent ion-toolbar.box-shadow {
  box-shadow: none !important;
}

/* Target all toolbar background variations for footer */
ion-footer.footer-transparent ion-toolbar,
ion-footer.footer-transparent ion-toolbar.box-shadow {
  box-shadow: none !important;
  background: transparent !important;
  background-image: none !important;
}

/* Use ::part() to target shadow DOM background */
ion-footer.footer-transparent ion-toolbar::part(background) {
  background: transparent !important;
  background-image: none !important;
  background-color: transparent !important;
  box-shadow: none !important;
}

.page-transition-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.responsive-main {
  width: 100%;
  flex: 1;
  display: flex;
  flex-direction: column;
  transition:
    max-width 0.3s ease,
    padding 0.3s ease;
}

/*
 * Shared floating bottom-nav clearance for pages that opt in via the
 * `tab-padding` prop (home / all / me). Sized to the DS BottomNav:
 * ~64px nav height + ~16px breathing room + the iOS home-indicator safe
 * area the nav sits within. Opted-in pages must NOT add their own bottom
 * padding on top of this. (Library manages its own clearance and does
 * not use this class.)
 *
 * The doubled `.responsive-main.responsive-main--tab-padding` selector is
 * deliberate: the responsive `@media` rules below set `.responsive-main {
 * padding: 0 }`, which has equal specificity and comes later in source — a
 * single-class selector here would be clobbered (padding-bottom reset to 0)
 * on every viewport, leaving the fixed nav covering content. The extra class
 * raises specificity so the bottom clearance always wins.
 */
.responsive-main.responsive-main--tab-padding {
  padding-bottom: calc(80px + var(--ion-safe-area-bottom, env(safe-area-inset-bottom, 0px)));
}

@media (max-width: 767px) {
  .responsive-main {
    padding: 0;
  }
}

@media (min-width: 768px) and (max-width: 1023px) {
  .responsive-main {
    max-width: 896px;
    margin-left: auto;
    margin-right: auto;
    padding: 0;
  }
}

@media (min-width: 1024px) {
  .responsive-main {
    max-width: 1280px;
    margin-left: auto;
    margin-right: auto;
    padding: 0;
  }
}
</style>

<style>
/* Global styles to override Ionic's shadow DOM - footer transparency */
.footer-transparent {
  --background: transparent !important;
  background: transparent !important;
}

ion-footer.footer-transparent {
  --background: transparent !important;
  background: transparent !important;
  --ion-background-color: transparent !important;
  padding: 0 !important;
}

ion-footer.footer-transparent::part(background) {
  background: transparent !important;
}

/* Header transparency — lets the slotted header own its own background */
.header-transparent {
  --background: transparent !important;
  background: transparent !important;
}

ion-header.header-transparent {
  --background: transparent !important;
  background: transparent !important;
  --ion-background-color: transparent !important;
}

ion-header.header-transparent::part(background) {
  background: transparent !important;
}

/* Suppress Ionic's default header bottom shadow/border (md mode) */
ion-header.header-transparent::after {
  display: none !important;
  background: transparent !important;
}

ion-content.master::part(scroll) {
  display: flex;
  flex-direction: column;
  height: 100%;
}

/*
 * Pages drawing their own scroll indicator (ScrollProgressBar) suppress the
 * platform one, which iOS otherwise overlays on top of it while a finger is
 * down — two bars saying the same thing.
 */
ion-content.hide-native-scrollbar::part(scroll) {
  scrollbar-width: none;
}

ion-content.hide-native-scrollbar::part(scroll)::-webkit-scrollbar {
  display: none;
}
</style>
